import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faCircleChevronLeft,
  faEye,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FileItem, File } from '@types';
import { ReactNode, useState } from 'react';
import { useRollbar } from '@rollbar/react';
import { Button } from 'react-daisyui';
import { FileItemExtended } from '@/components/UsersAccordionList/EditPermissionsModalSteps/interfaces';
import { hasSubItems } from '@/components/UsersAccordionList/EditPermissionsModalSteps/utils';
import './SelectFileOrFolderAccordion.css';
import { FilePreviewModal } from '@/components/FileBrowser/modals';
import { fetchUrl, processDownloadUrl } from '@/utils';
import { alertErrorMessage } from '@/utils/alerts';
import useLogEvent from '@/utils/logEvent';
import { IUserContext, useUserContext } from '@/context/UserContext';

interface Props {
  clientId: number;
  file: FileItem;
  level: number;
  open: boolean;
  fatherIsOpen: boolean;
  showAccordionIcon: boolean;
  onSelectFile: (file: FileItem) => void;
  /**
   * The ids of the files that are currently chosen, when the page wants a box on every file. Both
   * this and onSelectedFileChange are optional so that a page choosing one file at a time
   * (InvestorReportCardNewStatus, through SelectFileComponent) draws the tree exactly as it did
   * before: with neither prop, no box is drawn and the check button beside a file is the only way
   * to choose one.
   */
  selectedFileIds?: ReadonlySet<string>;
  /** Called with the file whose box was clicked, to be added to or taken out of the ids above. */
  onSelectedFileChange?: (file: FileItem) => void;
  /**
   * Drawn at the right end of a file's row, between the name and the eye that previews the file,
   * for the pages that want a control of their own on a row. AddDocumentModal passes the document
   * type select of a file that is ticked, and nothing for a file that is not.
   */
  renderFileControl?: (file: FileItem) => ReactNode;
  /**
   * False for a file whose box is drawn disabled, so it cannot be ticked. AddDocumentModal passes
   * it to disable the box of a file too large to read. Left out, every file's box can be ticked.
   */
  fileCanBeSelected?: (file: FileItem) => boolean;
}

export function SelectFileOrFolderAccordion({
  clientId,
  file,
  level,
  open,
  fatherIsOpen,
  showAccordionIcon,
  onSelectFile,
  selectedFileIds,
  onSelectedFileChange,
  renderFileControl,
  fileCanBeSelected,
}: Props) {
  const { auth }: IUserContext = useUserContext(); // Use the context
  const rollbar = useRollbar();
  const [isOpen, setIsOpen] = useState<boolean>(open);
  const [fileToPreview, setFileToPreview] = useState<boolean>(false);
  const childFatherIsOpen = fatherIsOpen && isOpen;
  const { logEvent } = useLogEvent();

  /**
   * True when the page asked for a box on every file by passing onSelectedFileChange. The row is
   * laid out differently then, and only then, so that a page choosing one file at a time
   * (InvestorReportCardNewStatus, through SelectFileComponent) draws the tree exactly as before:
   *
   * - the box comes first, before the icon and the name, as it does in Gmail, Drive and Finder;
   * - the check button is left out, because it chooses the same file the box chooses;
   * - the name is one line, cut with an ellipsis, and the whole of it is in the row's tooltip.
   */
  const rowHasSelectionBox = onSelectedFileChange !== undefined;

  const downloadFile = () => {
    fetchUrl(file.id, file.teamId)
      .then(async (url) => {
        await processDownloadUrl(url, file.name);
        await logEvent(file, clientId, auth?.role as string);
      })
      .catch((err) => rollbar.log(err as Error));
  };

  return (
    <>
      {fileToPreview ? (
        <FilePreviewModal
          isOpen={fileToPreview}
          fileItem={file as File}
          onClose={() => {
            setFileToPreview(false);
          }}
          onDownload={() => downloadFile()}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onError={() => alertErrorMessage('File not found')}
        />
      ) : null}
      {/* The blank line between rows is what made three-line names look like three rows. A row
          with a box sets its own height with py-1 instead. */}
      {rowHasSelectionBox ? null : <br />}
      <div
        className='flex items-start custom-accordion'
        key={`div-main-${file.id}-${level}`}
      >
        <input
          type='checkbox'
          id={`checkbox-accordion-${file.id}-${level}`}
          className='custom-accordion-checkbox'
          checked={isOpen}
          onChange={() => {}}
        />
        <div
          key={`div-folder-${file.id}-${level}`}
          className={`accordion-sibling ${
            level === 0 ? 'pl-0' : 'pl-6'
          } w-[95%]`}
        >
          <label
            className={
              rowHasSelectionBox
                ? 'flex items-center gap-2 py-1 pr-1 rounded hover:bg-base-200 custom-accordion-label'
                : 'flex items-center custom-accordion-label'
            }
            htmlFor={`checkbox-accordion-${file.id}-${level}`}
            key={`label-folder-${file.id}-${level}`}
          >
            {/* The whole row is a <label htmlFor> for the hidden checkbox above that opens and
                closes the folder, so a click anywhere in it opens the folder unless the click is
                stopped — the same reason the buttons below stop theirs. */}
            {rowHasSelectionBox && file.type === 'file' ? (
              <input
                type='checkbox'
                className='checkbox checkbox-xs checkbox-accent shrink-0'
                aria-label={`Select ${file.name}`}
                key={`file-checkbox-${file.id}-${level}`}
                checked={selectedFileIds?.has(file.id) ?? false}
                disabled={fileCanBeSelected ? !fileCanBeSelected(file) : false}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onSelectedFileChange(file)}
              />
            ) : null}
            {/* A folder has no box, so it gets the width of one and its icon lines up with the
                icons of the files under it. */}
            {rowHasSelectionBox && file.type !== 'file' ? (
              <span className='w-4 shrink-0' />
            ) : null}
            <FontAwesomeIcon
              icon={file.type === 'folder' ? faFolder : faFile}
              size={rowHasSelectionBox ? 'sm' : 'lg'}
              className={rowHasSelectionBox ? 'shrink-0 opacity-60' : 'px-2'}
            />
            <h3
              key={`folder-name-${file.id}-${level}`}
              className={
                rowHasSelectionBox
                  ? 'text-sm flex-1 min-w-0 truncate'
                  : 'text-base ml-2 break-all max-w-xs'
              }
              // The name is cut to one line when the row has a box, so the whole of it has to be
              // readable somewhere: hovering the row shows it.
              title={rowHasSelectionBox ? file.name : undefined}
            >
              {file.name}
            </h3>
            {file.type === 'file' ? (
              <div className='flex items-center justify-end'>
                {/* The row is a <label htmlFor> for the hidden checkbox that opens and closes the
                    folder, so a click on the control the page put here would open the folder; the
                    click is stopped for the same reason the buttons below stop theirs. */}
                {renderFileControl ? (
                  <div
                    className='shrink-0 mr-2'
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role='presentation'
                    key={`file-control-${file.id}-${level}`}
                  >
                    {renderFileControl(file)}
                  </div>
                ) : null}
                <Button
                  size='xs'
                  variant={rowHasSelectionBox ? undefined : 'outline'}
                  key={`file-preview-${file.id}-${level}`}
                  className={
                    rowHasSelectionBox
                      ? 'btn-ghost shrink-0 cursor-pointer opacity-50 hover:opacity-100'
                      : 'menu-hover btn-accent group cursor-pointer ml-2'
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setFileToPreview(true);
                  }}
                >
                  <FontAwesomeIcon
                    data-testid='preview-file'
                    icon={faEye}
                    title={rowHasSelectionBox ? 'Preview file' : undefined}
                    size={rowHasSelectionBox ? 'sm' : 'lg'}
                    className={rowHasSelectionBox ? '' : 'text-accent'}
                  />
                </Button>
                {/* Left out when the row has a box, because the box already chooses this file and
                    two controls for one action is what made the row hard to read. */}
                {rowHasSelectionBox ? null : (
                  <Button
                    size='xs'
                    variant='outline'
                    key={`file-select-${file.id}-${level}`}
                    className='menu-hover btn-accent group cursor-pointer ml-2'
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectFile(file);
                    }}
                  >
                    <FontAwesomeIcon
                      data-testid='grant-permissions-modal'
                      icon={faCheck}
                      title='Select File'
                      size='lg'
                      className='text-accent'
                    />
                  </Button>
                )}
              </div>
            ) : null}
          </label>
          <div className='custom-accordion-content'>
            {file.files?.map((subfolder) => (
              <SelectFileOrFolderAccordion
                clientId={clientId}
                file={subfolder}
                level={level + 1}
                open={level + 1 <= 1}
                fatherIsOpen={childFatherIsOpen}
                showAccordionIcon={hasSubItems(subfolder as FileItemExtended)}
                onSelectFile={onSelectFile}
                selectedFileIds={selectedFileIds}
                onSelectedFileChange={onSelectedFileChange}
                renderFileControl={renderFileControl}
                fileCanBeSelected={fileCanBeSelected}
                key={`select-file-component-${subfolder.id}`}
              />
            ))}
          </div>
        </div>
        <div
          className=' text-right flex absolute right-4 w-[5%]'
          key={`div-accordion-${file.id}-${level}`}
        >
          {showAccordionIcon ? (
            <FontAwesomeIcon
              icon={faCircleChevronLeft}
              onClick={() => setIsOpen(!isOpen)}
              className={`text-base cursor-pointer transform duration-100 transition ease-linear h-[24px] ${
                isOpen ? '-rotate-90' : ''
              } ${fatherIsOpen ? '' : 'hidden'}`}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}
